import { isVersionName } from '@readme/iso';
import { useCallback } from 'react';
import { matchPath } from 'react-router-dom';

import useBuildRoute from '@core/hooks/useBuildRoute';
import { useProjectStore } from '@core/store';

import { useSuperHubContext } from '@routes/SuperHub/components/Context';
import type { SuperHubRouteParams } from '@routes/SuperHub/types';
import { SuperHubRoutePaths } from '@routes/SuperHub/types';

/**
 * Generates a function that when called returns a URL for the provided branch
 * name that is based off the current route location path and hash. Use this to
 * redirect users from one branch to another. Route slug segments are removed
 * because not every branch will have equivalent pages. So we defer to the
 * section and let users be redirected to the first page of that section.
 * @example
 * ```ts
 * // -> /docs/child-page#/appearance/theme-editor
 * const buildVersionPath = useBuildBranchPath();
 * const redirectToVersionUrl = buildVersionPath('2.0');
 * window.location.assign(redirectToVersionUrl);
 * // -> /v2.0/docs#/appearance/theme-editor
 * ```
 */
export default function useBuildBranchPath() {
  const defaultVersion = useProjectStore(s => s.data.default_version.name);

  const { browserRouterLocation } = useSuperHubContext();
  const { pathname, hash } = browserRouterLocation;
  const match = matchPath<SuperHubRouteParams>(pathname, Object.values(SuperHubRoutePaths));
  const { action: actionSegment, section: sectionSegment } = match?.params ?? {};

  const buildRoute = useBuildRoute();

  return useCallback(
    /**
     * Returns a relative path given the provided branch name that is used to
     * redirect users from one branch to another.
     */
    (branchName: string) => {
      /**
       * Path construction logic here mirrors the Hub header's branch dropdown
       * @see packages/react/src/Hub/Header/utils/hooks.js
       */
      const branchSegment = defaultVersion !== branchName && `v${encodeURIComponent(branchName)}`;
      let segments = [branchSegment, actionSegment, sectionSegment];

      // Versions can't `review`, so if we're redirecting on the review tab let's go to editing the docs instead
      if (actionSegment === 'review' && isVersionName(branchName.startsWith('v') ? branchName : `v${branchName}`)) {
        segments = [branchSegment, 'update', sectionSegment || 'docs'];
      }

      return buildRoute({
        includeBaseUrl: true,
        hash,
        pathnameSegments: segments,
      });
    },
    [defaultVersion, buildRoute, hash, actionSegment, sectionSegment],
  );
}
